import {
  ALLERGY_CATEGORIES,
  ALLERGY_CRITICALITIES,
  DUPLICATE_OVERRIDE_REASON_MIN_LENGTH,
  DUPLICATE_REASONS,
  IDENTIFIER_SYSTEMS,
  identifierNeedsIssuingCountry,
  normaliseIdentifier,
  PATIENT_IDENTITIES,
  VISIT_TYPES,
} from '@wodi/domain';
import { z } from 'zod';

import {
  actorSchema,
  ageSchema,
  codedRefSchema,
  codedValueSchema,
  isoDaySchema,
  msisdnSchema,
  optionallyCodedValueSchema,
  reasonSchema,
  RECORD_STATUSES,
} from './clinical-common.ts';
import { OUTBOX_STATUSES } from './integrations.ts';
import { PAYER_KINDS } from './visits.ts';

// Patients: registration, search, duplicate check, the banner and corrections
// (docs/plans/phase1-registration.md §3; ADR 0008).
//
// Search terms and demographics are PHI. Every request carrying them is a POST or PATCH
// with a JSON body, never a query string. Response bodies carry patient data only to
// callers holding `patients.read`; error bodies never do.

// --- Value lists --------------------------------------------------------------------

export { IDENTIFIER_SYSTEMS };

export const IDENTIFIER_VERIFICATIONS = [
  'unverified',
  'document_seen',
  'registry_verified',
] as const;
export const DEMOGRAPHIC_SOURCES = ['reception', 'client_registry', 'import'] as const;
export const CONTACT_OWNERS = ['patient', 'someone_else'] as const;
export const RELATIONSHIP_STATUSES = ['active', 'ended', 'entered_in_error'] as const;
export const CONSENT_PURPOSES = ['hie_data_sharing'] as const;
/**
 * Purposes a recorded decision can have. Patient portal consent is recorded by the portal's
 * own enrolment (docs/plans/phase3-portal-feedback-kiswahili.md §2.1), never by the HIE
 * consent screens, so it is in history and records but not in CONSENT_PURPOSES.
 */
export const CONSENT_RECORD_PURPOSES = ['hie_data_sharing', 'patient_portal'] as const;
export const CONSENT_DECISIONS = ['granted', 'refused', 'withdrawn'] as const;
/** Only verbal consent recorded by reception is built for now (plan §12, decision 3). */
export const CONSENT_METHODS = ['verbal'] as const;
/** `merged`: an unknown record merged into the identified patient's (ADR 0024). */
export const PATIENT_STATUSES = ['active', 'entered_in_error', 'merged'] as const;
export { PATIENT_IDENTITIES };
export const REGISTRY_SYNC_STATES = [
  'not_requested',
  'blocked_no_consent',
  'pending_create',
  'pending_update',
  'linked',
  'failed',
  'needs_review',
] as const;
export const SEARCH_MATCH_KINDS = [
  'identifier',
  'patient_number',
  'phone',
  'name',
  // An unknown patient's temporary label (docs/plans/phase2-casualty.md §4).
  'temporary_label',
] as const;

export type IdentifierVerification = (typeof IDENTIFIER_VERIFICATIONS)[number];
export type RegistrySyncState = (typeof REGISTRY_SYNC_STATES)[number];
export type SearchMatchKind = (typeof SEARCH_MATCH_KINDS)[number];

// --- Pieces of a patient, as sent -----------------------------------------------------

export const personNameInputSchema = z.object({
  given: z.string().trim().min(1).max(100),
  middle: z.string().trim().max(100).nullable().default(null),
  family: z.string().trim().max(100).nullable().default(null),
});

/**
 * Date of birth, exact or estimated (plan §12, decisions 9 and 12). One of the two is
 * always required. An estimated age is years from 2, months under 2; the server turns it
 * into an estimated date with `estimatedBirthDate` on the East Africa day of the request.
 */
export const birthInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('exact'), birthDate: isoDaySchema }),
  z.object({
    kind: z.literal('estimated'),
    age: z.object({
      unit: z.enum(['years', 'months']),
      value: z.number().int().min(0).max(130),
    }),
  }),
]);

export const residenceInputSchema = z.object({
  county: optionallyCodedValueSchema.nullable().default(null),
  subCounty: optionallyCodedValueSchema.nullable().default(null),
  ward: optionallyCodedValueSchema.nullable().default(null),
  /** Village or estate, free text. */
  locality: z.string().trim().max(200).nullable().default(null),
});

export const identifierInputSchema = z
  .object({
    system: z.enum(IDENTIFIER_SYSTEMS),
    /** As typed; the server stores it and its normalised form. */
    value: z.string().max(100),
    /** ISO 3166-1 alpha-2, passports only. */
    issuingCountry: z
      .string()
      .regex(/^[A-Z]{2}$/, 'Must be a two-letter country code')
      .nullable()
      .default(null),
  })
  .superRefine((identifier, ctx) => {
    const normalised = normaliseIdentifier(identifier.system, identifier.value);
    if (!normalised.ok) ctx.addIssue({ code: 'custom', path: ['value'], message: normalised.code });
    const needsCountry = identifierNeedsIssuingCountry(identifier.system);
    if (needsCountry && identifier.issuingCountry === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['issuingCountry'],
        message: 'A passport needs its issuing country',
      });
    }
    if (!needsCountry && identifier.issuingCountry !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['issuingCountry'],
        message: 'Only a passport has an issuing country',
      });
    }
  });

export const contactInputSchema = z.object({
  kind: z.literal('mobile').default('mobile'),
  value: msisdnSchema,
  /** Families share phones; a child's number is often a parent's. */
  belongsTo: z.enum(CONTACT_OWNERS),
  isPrimary: z.boolean().default(false),
});

/** A related person who is not a patient here (typical next of kin). */
export const relatedPersonInputSchema = z.object({
  givenName: z.string().trim().min(1).max(100),
  familyName: z.string().trim().max(100).nullable().default(null),
  phone: msisdnSchema.nullable().default(null),
});

export const addRelationshipRequestSchema = z
  .object({
    /** When the other person is a patient in this organisation. */
    relatedPatientId: z.uuid().nullable().default(null),
    relatedPerson: relatedPersonInputSchema.nullable().default(null),
    /** From the tenant's relationship value set (TODO(verify-spec): the national set). */
    relationship: codedRefSchema,
    isNextOfKin: z.boolean().default(false),
    /** The patient is a dependant of `relatedPatientId`. */
    isDependant: z.boolean().default(false),
  })
  .refine((r) => (r.relatedPatientId === null) !== (r.relatedPerson === null), {
    message: 'Give either a patient or a person, not both',
  })
  .refine((r) => !r.isDependant || r.relatedPatientId !== null, {
    message: 'A dependant is registered against a patient',
    path: ['isDependant'],
  });

export const nextOfKinInputSchema = z
  .object({
    relatedPatientId: z.uuid().nullable().default(null),
    relatedPerson: relatedPersonInputSchema.nullable().default(null),
    relationship: codedRefSchema,
  })
  .refine((r) => (r.relatedPatientId === null) !== (r.relatedPerson === null), {
    message: 'Give either a patient or a person, not both',
  });

export const consentInputSchema = z.object({
  purpose: z.enum(CONSENT_PURPOSES).default('hie_data_sharing'),
  decision: z.enum(['granted', 'refused']),
  method: z.enum(CONSENT_METHODS),
  /** The exact wording read to the patient (a tenant consent text). */
  consentTextId: z.uuid(),
});

export const duplicateOverrideSchema = z.object({
  /** Every candidate the receptionist saw and judged to be someone else. */
  candidateIds: z.array(z.uuid()).min(1).max(50),
  reason: z.string().trim().min(DUPLICATE_OVERRIDE_REASON_MIN_LENGTH).max(500),
});

function atMostOnePrimary(contacts: readonly { isPrimary: boolean }[]): boolean {
  return contacts.filter((c) => c.isPrimary).length <= 1;
}

export const registerPatientRequestSchema = z.object({
  /** Generated once per form; a retried request returns the same patient. */
  clientRequestId: z.uuid(),
  name: personNameInputSchema,
  sex: codedRefSchema,
  birth: birthInputSchema,
  residence: residenceInputSchema.nullable().default(null),
  identifiers: z.array(identifierInputSchema).max(10).default([]),
  contacts: z
    .array(contactInputSchema)
    .max(5)
    .default([])
    .refine(atMostOnePrimary, 'Only one phone number can be the main one'),
  nextOfKin: z.array(nextOfKinInputSchema).max(5).default([]),
  dependantOf: z
    .object({ patientId: z.uuid(), relationship: codedRefSchema })
    .nullable()
    .default(null),
  /**
   * HIE data-sharing consent, when it was asked. Absent when the consent wording isn't
   * configured yet or the question wasn't asked; the registry link then waits.
   */
  consent: consentInputSchema.nullable().default(null),
  duplicateOverride: duplicateOverrideSchema.nullable().default(null),
});

export type RegisterPatientRequest = z.infer<typeof registerPatientRequestSchema>;

// --- Pieces of a patient, as returned -------------------------------------------------

/** Sex as stored, plus the value set's short form for the banner ("F · 34 y"). */
export const sexSchema = codedValueSchema.extend({ abbreviation: z.string().nullable() });

export const facilityRefSchema = z.object({ id: z.uuid(), name: z.string() });

export const patientIdentifierSchema = z.object({
  id: z.uuid(),
  system: z.enum(IDENTIFIER_SYSTEMS),
  /** As entered; shown in the mono face. */
  value: z.string(),
  issuingCountry: z.string().nullable(),
  source: z.enum(DEMOGRAPHIC_SOURCES),
  verification: z.enum(IDENTIFIER_VERIFICATIONS),
  status: z.enum(RECORD_STATUSES),
  statusReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

export const patientContactSchema = z.object({
  id: z.uuid(),
  kind: z.literal('mobile'),
  value: msisdnSchema,
  belongsTo: z.enum(CONTACT_OWNERS),
  isPrimary: z.boolean(),
  status: z.enum(RECORD_STATUSES),
  statusReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

export const patientRefSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  patientNumber: z.string(),
});

export const patientRelationshipSchema = z.object({
  id: z.uuid(),
  /** The subject: for a dependant, the dependant. */
  patientId: z.uuid(),
  relatedPatient: patientRefSchema.nullable(),
  relatedPerson: z
    .object({
      givenName: z.string(),
      familyName: z.string().nullable(),
      phone: msisdnSchema.nullable(),
    })
    .nullable(),
  relationship: codedValueSchema,
  isNextOfKin: z.boolean(),
  isDependant: z.boolean(),
  status: z.enum(RELATIONSHIP_STATUSES),
  statusReason: z.string().nullable(),
  endedAt: z.iso.datetime().nullable(),
});

export const patientConsentSchema = z.object({
  id: z.uuid(),
  purpose: z.enum(CONSENT_RECORD_PURPOSES),
  decision: z.enum(CONSENT_DECISIONS),
  method: z.enum(CONSENT_METHODS),
  consentTextId: z.uuid(),
  consentTextVersion: z.string(),
  /** Why the patient withdrew (required) or refused (optional), in their words. */
  reason: z.string().nullable(),
  facility: facilityRefSchema,
  capturedAt: z.iso.datetime(),
  capturedBy: actorSchema,
});

/**
 * The Client Registry sync state, with what to tell the person (CLAUDE.md: every
 * integration shows waiting, success, or failed with reason and next step).
 */
export const registrySyncStatusSchema = z.object({
  state: z.enum(REGISTRY_SYNC_STATES),
  outboxStatus: z.enum(OUTBOX_STATUSES).nullable(),
  /** A stable code, never registry text. */
  errorCode: z.string().nullable(),
  lastSyncedAt: z.iso.datetime().nullable(),
  /** Plain words, e.g. "Waiting to send to the national registry". */
  message: z.string(),
});

export type RegistrySyncStatus = z.infer<typeof registrySyncStatusSchema>;

export const allergySummaryItemSchema = z.object({
  id: z.uuid(),
  substance: z.string(),
  reaction: z.string().nullable(),
  category: z.enum(ALLERGY_CATEGORIES).nullable(),
  criticality: z.enum(ALLERGY_CRITICALITIES).nullable(),
  verificationStatus: z.enum(['unconfirmed', 'confirmed']),
});

/**
 * The banner's allergy state (CLAUDE.md: never blank). There is no empty variant: a list
 * has at least one allergy, otherwise the state is "No known allergies" or "Allergies not
 * recorded". Mirrors `summariseAllergies` in @wodi/domain and `BannerAllergies` in @wodi/ui.
 */
export const allergySummarySchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('list'), allergies: z.array(allergySummaryItemSchema).min(1) }),
  z.object({ state: z.literal('no_known_allergies') }),
  z.object({ state: z.literal('not_recorded') }),
]);

export type AllergySummaryView = z.infer<typeof allergySummarySchema>;

function codedOrTextSchema() {
  return z
    .object({ system: z.string().nullable(), code: z.string().nullable(), display: z.string() })
    .nullable();
}

export const residenceSchema = z.object({
  county: codedOrTextSchema(),
  subCounty: codedOrTextSchema(),
  ward: codedOrTextSchema(),
  locality: z.string().nullable(),
});

export const personNameSchema = z.object({
  given: z.string(),
  middle: z.string().nullable(),
  family: z.string().nullable(),
});

/**
 * Whether we know who the patient is (docs/plans/phase2-casualty.md §2.2). An unknown
 * patient carries a temporary label, shown in the mono face, and the banner says
 * "Not identified".
 */
export const patientIdentitySchema = z.object({
  state: z.enum(PATIENT_IDENTITIES),
  temporaryLabel: z.string().nullable(),
});

export type PatientIdentityView = z.infer<typeof patientIdentitySchema>;

/** An unknown record merged into this patient's (ADR 0024). */
export const mergedFromSchema = z.object({
  patientId: z.uuid(),
  temporaryLabel: z.string(),
  patientNumber: z.string(),
  mergedAt: z.iso.datetime(),
});

/**
 * A recorded death of this person, under this record or one merged into it
 * (docs/plans/phase2-mortuary-and-notifications.md §1.1 item 2). Null while alive, or when a
 * death record was entered in error.
 */
export const deceasedSchema = z.object({ deathId: z.uuid(), diedAt: z.iso.datetime() });
export type DeceasedView = z.infer<typeof deceasedSchema>;

export const patientDetailSchema = z.object({
  id: z.uuid(),
  patientNumber: z.string(),
  /** For optimistic concurrency: send it back as `expectedVersion` when correcting. */
  version: z.number().int(),
  status: z.enum(PATIENT_STATUSES),
  statusReason: z.string().nullable(),
  name: personNameSchema,
  displayName: z.string(),
  sex: sexSchema,
  birthDate: isoDaySchema.nullable(),
  birthDateEstimated: z.boolean(),
  /** On the East Africa day of the request; estimated ages are never in days. */
  age: ageSchema.nullable(),
  residence: residenceSchema.nullable(),
  registeredFacility: facilityRefSchema,
  identifiers: z.array(patientIdentifierSchema),
  contacts: z.array(patientContactSchema),
  nextOfKin: z.array(patientRelationshipSchema),
  /** People registered as this patient's dependants. */
  dependants: z.array(patientRelationshipSchema),
  /** The patients this patient is a dependant of. */
  principals: z.array(patientRelationshipSchema),
  /** The current HIE data-sharing decision, or null when never asked. */
  consent: patientConsentSchema.nullable(),
  registrySync: registrySyncStatusSchema,
  allergies: allergySummarySchema,
  identity: patientIdentitySchema,
  /** Where found, clothing, marks, for an unknown patient. PHI. */
  unknownDescription: z.string().nullable(),
  /** Set when this unknown record was merged into an identified patient's. */
  mergedInto: z
    .object({
      patientId: z.uuid(),
      displayName: z.string(),
      patientNumber: z.string(),
      mergedAt: z.iso.datetime(),
    })
    .nullable(),
  /** Unknown records merged into this one; their history is shown here. */
  mergedFrom: z.array(mergedFromSchema),
  deceased: deceasedSchema.nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type PatientDetail = z.infer<typeof patientDetailSchema>;

// --- Search -----------------------------------------------------------------------------

/** POST, never GET: the query is PHI and must not reach a URL or a log. */
export const patientSearchRequestSchema = z.object({
  query: z.string().trim().min(2).max(100),
  birthDate: isoDaySchema.optional(),
  sex: codedRefSchema.optional(),
  limit: z.number().int().min(1).max(20).default(20),
});

/** Enough to tell two people apart (DESIGN.md §6: name, sex, age, phone last 4, number). */
export const patientSearchResultSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  sex: sexSchema,
  age: ageSchema.nullable(),
  ageEstimated: z.boolean(),
  phoneLast4: z
    .string()
    .regex(/^\d{4}$/)
    .nullable(),
  patientNumber: z.string(),
  matchedOn: z.array(z.enum(SEARCH_MATCH_KINDS)).min(1),
  /** The person has a recorded death; results say "Deceased". */
  deceased: z.boolean(),
  /** Patients are shared across branches; a sister branch's patient is labelled with it. */
  registeredFacility: facilityRefSchema,
  registeredHere: z.boolean(),
});

export type PatientSearchResult = z.infer<typeof patientSearchResultSchema>;

export const patientSearchResponseSchema = z.object({
  items: z.array(patientSearchResultSchema),
});

// --- Duplicate check -------------------------------------------------------------------

export const duplicateCheckRequestSchema = z.object({
  name: personNameInputSchema,
  birth: birthInputSchema.nullable().default(null),
  identifiers: z.array(identifierInputSchema).max(10).default([]),
  phones: z.array(msisdnSchema).max(5).default([]),
  /** Set when registering a dependant. */
  principalPatientId: z.uuid().nullable().default(null),
});

export const DUPLICATE_REASON_LIST = DUPLICATE_REASONS;

const duplicatePatientSchema = patientSearchResultSchema.omit({ matchedOn: true });

export const identifierConflictSchema = z.object({
  patient: duplicatePatientSchema,
  system: z.enum(IDENTIFIER_SYSTEMS),
});

export const duplicateCandidateSchema = z.object({
  patient: duplicatePatientSchema,
  reasons: z.array(z.enum(DUPLICATE_REASONS)).min(1),
});

export const duplicateCheckResponseSchema = z.object({
  /** Can't be overridden: use that patient or correct the identifier. */
  blocking: z.array(identifierConflictSchema),
  /** Each needs "Use this patient" or "This is a different person" with a reason. */
  candidates: z.array(duplicateCandidateSchema),
});

export type DuplicateCheckResponse = z.infer<typeof duplicateCheckResponseSchema>;

// --- Banner -----------------------------------------------------------------------------

/** Everything the patient banner shows (CLAUDE.md; DESIGN.md §6.2). */
export const patientBannerSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  sex: sexSchema,
  age: ageSchema.nullable(),
  ageEstimated: z.boolean(),
  patientNumber: z.string(),
  crId: z.string().nullable(),
  registrySync: registrySyncStatusSchema,
  openVisit: z
    .object({
      id: z.uuid(),
      visitNumber: z.string(),
      type: z.enum(VISIT_TYPES),
      payerKind: z.enum(PAYER_KINDS),
      /**
       * For SHA visits, cover and SHA visit state in words, e.g. "SHIF covered · SHA visit
       * started" (docs/plans/phase1-sha.md §9.2). Null for other payers.
       */
      payerDetail: z.string().nullable(),
    })
    .nullable(),
  allergies: allergySummarySchema,
  /** "Not identified" and the temporary label for an unknown patient (casualty plan §1.1). */
  identity: patientIdentitySchema,
  /**
   * Unknown records merged into this patient. A banner asked for by a merged record's ID
   * is this patient's banner (ADR 0024).
   */
  mergedFrom: z.array(mergedFromSchema),
  /** "Deceased" and the time of death, on every patient screen (mortuary plan §1.1). */
  deceased: deceasedSchema.nullable(),
  /**
   * An open pregnancy the reader may see (docs/plans/phase2-maternity-mch.md §1.1 item 11):
   * the EDD and weeks and days today, never a category. Null otherwise.
   */
  pregnancy: z
    .object({
      edd: z.string().nullable(),
      gestation: z
        .object({ weeks: z.number().int(), days: z.number().int(), totalDays: z.number().int() })
        .nullable(),
      /**
       * A passive "Past EDD, check pregnancy status" cue: today is more than the facility's
       * set number of weeks past the EDD (TODO(clinical-verify) M23; no cue until set).
       */
      pastEddCue: z.boolean(),
    })
    .nullable(),
  /**
   * Isolation and precautions in place, in words ("Contact precautions"), from flags the
   * reader may see (docs/plans/phase3-operations-modules.md §2.1). Passive; never blocks.
   */
  precautions: z.array(z.string()),
});

export type PatientBanner = z.infer<typeof patientBannerSchema>;

// --- Corrections ------------------------------------------------------------------------

/**
 * Only the fields sent change. Correcting a name, sex or date of birth needs a reason
 * (plan §3.3), because those are how a person is identified.
 */
export const updatePatientRequestSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    name: personNameInputSchema.optional(),
    sex: codedRefSchema.optional(),
    birth: birthInputSchema.optional(),
    residence: residenceInputSchema.nullable().optional(),
    changeReason: reasonSchema.optional(),
  })
  .refine(
    (r) =>
      (r.name === undefined && r.sex === undefined && r.birth === undefined) ||
      r.changeReason !== undefined,
    { message: 'Correcting a name, sex or date of birth needs a reason', path: ['changeReason'] },
  );

export const addIdentifierRequestSchema = identifierInputSchema.and(
  z.object({ verification: z.enum(['unverified', 'document_seen']).default('unverified') }),
);
export const addContactRequestSchema = contactInputSchema;
export const endRelationshipRequestSchema = z.object({
  reason: reasonSchema,
  /** True when the relationship was recorded by mistake, not when it has ended. */
  enteredInError: z.boolean(),
});
/**
 * A change of mind is a new decision, never an edit. Withdrawing needs a reason (it stops
 * sharing that was agreed, so the record says why); a refusal may carry one.
 */
export const recordConsentRequestSchema = z
  .object({
    purpose: z.enum(CONSENT_PURPOSES).default('hie_data_sharing'),
    decision: z.enum(CONSENT_DECISIONS),
    method: z.enum(CONSENT_METHODS),
    consentTextId: z.uuid(),
    reason: reasonSchema.nullable().default(null),
  })
  .refine((c) => c.decision !== 'withdrawn' || c.reason !== null, {
    message: 'Give the reason the patient withdrew consent',
    path: ['reason'],
  })
  .refine((c) => c.decision !== 'granted' || c.reason === null, {
    message: 'A reason is only recorded for a refusal or a withdrawal',
    path: ['reason'],
  });

/** `GET /patients/:patientId/consents`: every decision, newest first. */
export const patientConsentHistorySchema = z.object({
  items: z.array(patientConsentSchema),
});

// --- History ----------------------------------------------------------------------------

export const patientSnapshotSchema = z.object({
  name: personNameSchema,
  sex: codedValueSchema,
  birthDate: isoDaySchema.nullable(),
  birthDateEstimated: z.boolean(),
  residence: residenceSchema.nullable(),
  status: z.enum(PATIENT_STATUSES),
});

/** One change in a patient's history, newest first. */
export const patientHistoryEntrySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('demographics'),
    at: z.iso.datetime(),
    by: actorSchema,
    version: z.number().int(),
    source: z.enum(DEMOGRAPHIC_SOURCES),
    changeReason: z.string().nullable(),
    snapshot: patientSnapshotSchema,
  }),
  z.object({
    kind: z.literal('identifier'),
    at: z.iso.datetime(),
    by: actorSchema,
    change: z.enum(['added', 'entered_in_error']),
    identifier: patientIdentifierSchema,
  }),
  z.object({
    kind: z.literal('contact'),
    at: z.iso.datetime(),
    by: actorSchema,
    change: z.enum(['added', 'entered_in_error']),
    contact: patientContactSchema,
  }),
  z.object({
    kind: z.literal('relationship'),
    at: z.iso.datetime(),
    by: actorSchema,
    change: z.enum(['added', 'ended', 'entered_in_error']),
    relationship: patientRelationshipSchema,
  }),
  z.object({
    kind: z.literal('consent'),
    at: z.iso.datetime(),
    by: actorSchema,
    consent: patientConsentSchema,
  }),
]);

export type PatientHistoryEntry = z.infer<typeof patientHistoryEntrySchema>;
